import {
  Avatar,
  Col,
  Form,
  Layout,
  Menu,
  Popconfirm,
  Rate,
  Row,
} from "antd";

import React, { ReactElement, useEffect, useMemo, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  adminServices,
  getServicePaths,
  isAllowedService,
} from "../../../utils/services";
import { LOGIN_SUCCESS, SIDEBAR_COLLAPSE } from "../../../redux/auth/authType";
import { getImgUrl } from "../../../utils";
import LogoImg from "../../../images/logo.png";
import IconImg from "../../../images/favicon.svg";
import noImage from "../../../images/noImage.png";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BankOutlined,
  CarryOutOutlined,
  CodepenOutlined,
  ContainerOutlined,
  CopyOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  GiftOutlined,
  IdcardOutlined,
  ProductOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  SoundOutlined,
  SyncOutlined,
  TagOutlined,
  UnorderedListOutlined,
  WechatOutlined,
} from "@ant-design/icons";

const { Sider } = Layout;
const { SubMenu } = Menu;

const MainSidebar = (): ReactElement => {
  const { sidebarCollapse, type, shopInfo, merchantInfo } = useSelector(
    (state) => (state as any)?.authReducer
  );

  const location = useLocation();
  const pathname = location.pathname;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [visiblePopconfirm, setVisiblePopconfirm] = useState<any>(undefined);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>("");

  const setCollapsed = (value: boolean) => {
    dispatch({
      type: SIDEBAR_COLLAPSE,
      payload: {
        sidebarCollapse: value,
      },
    });
  };

  const showPopconfirm = (mobileNumber: any) => {
    setVisiblePopconfirm(mobileNumber);
  };

  const handleCancel = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setVisiblePopconfirm(undefined);
  };

  const onSetDefault = async (shopId: any) => {
    if (shopId) {
      dispatch({
        type: LOGIN_SUCCESS,
        payload: {
          shopInfo: null,
          iat: new Date(),
        },
      });
      navigate("/");
    }
  };

  const getBestMatchKey = (currentPath: string, keys: string[]) => {
    const matched = keys
      .filter((k) => currentPath === k || currentPath.startsWith(k + "/"))
      .sort((a, b) => b.length - a.length)[0];

    return matched || "";
  };

  const adminMenuKeys = useMemo(() => {
    const keys: string[] = [];

    adminServices?.forEach((service: any) => {
      if (service?.sub && service?.sub?.length) {
        const allowedSubItems = service?.sub?.filter((item: any) => {
          return item?.icon && isAllowedService(`${service.name} ${item.name}`);
        });

        if (
          allowedSubItems?.length === 1 &&
          service?.sub?.find((item: any) => item.isParent)
        ) {
          const actualPath = `${service?.path}${service?.sub?.[0]?.path}`;
          keys.push(actualPath);
        } else {
          service?.sub?.forEach((sub: any) => {
            const actualPath = `${service?.path}${sub?.path}`;
            if (!actualPath.includes(":") && sub?.icon) {
              keys.push(actualPath);
            }
          });
        }
      } else if (service?.path) {
        keys.push(service?.path);
      }
    });

    return keys;
  }, []);

  const shopMenuKeys = useMemo(() => {
    const shopId = shopInfo?.id;
    if (!shopId) return ["/"];

    return [
      "/",
      `/shops/${shopId}/myshop-orders`,
      `/shops/${shopId}/myshop-return-order`,

      `/shops/${shopId}/affiliate-products`,

      `/shops/${shopId}/my-shop-products`,
      `/shops/${shopId}/myshop-requested-products`,
      `/shops/${shopId}/myshop-product-catalogue`,
      `/shops/${shopId}/myshop-out-of-stock`,

      `/my-shop/${shopId}/unsettled-orders`,
      `/my-shop/${shopId}/settled-orders`,

      `/shops/${shopId}/myshop-categories`,

      `/shops/${shopId}/myshop-campaign`,
      `/shops/${shopId}/myshop-promo-code`,

      `/shops/${shopId}/myshop-details`,
      `/shops/${shopId}/shop-business-contract-info`,
      `/shops/${shopId}/shop-business-info`,
      `/shops/${shopId}/shop-banks`,

      `/shops/${shopId}/chats`,
    ];
  }, [shopInfo?.id]);
  const getShopOpenKey = (currentPath: string) => {
    const shopId = shopInfo?.id;
    if (!shopId) return "";

    if (
      currentPath.startsWith(`/shops/${shopId}/myshop-orders`) ||
      currentPath.startsWith(`/shops/${shopId}/myshop-return-order`)
    )
      return "sub4";

    if (
      currentPath.startsWith(`/shops/${shopId}/my-shop-products`) ||
      currentPath.startsWith(`/shops/${shopId}/myshop-requested-products`) ||
      currentPath.startsWith(`/shops/${shopId}/myshop-product-catalogue`) ||
      currentPath.startsWith(`/shops/${shopId}/myshop-out-of-stock`)
    )
      return "sub2";

    if (
      currentPath.startsWith(`/my-shop/${shopId}/unsettled-orders`) ||
      currentPath.startsWith(`/my-shop/${shopId}/settled-orders`)
    )
      return "sub20";

    if (
      currentPath.startsWith(`/shops/${shopId}/myshop-campaign`) ||
      currentPath.startsWith(`/shops/${shopId}/myshop-promo-code`)
    )
      return "sub3";

    if (
      currentPath.startsWith(`/shops/${shopId}/myshop-details`) ||
      currentPath.startsWith(`/shops/${shopId}/shop-business-contract-info`) ||
      currentPath.startsWith(`/shops/${shopId}/shop-business-info`) ||
      currentPath.startsWith(`/shops/${shopId}/shop-banks`)
    )
      return "sub1";

    return "";
  };

  useEffect(() => {
    const isAdmin = shopInfo?.id == null || shopInfo?.id === "";

    if (isAdmin) {
      const best = getBestMatchKey(pathname, adminMenuKeys);
      setSelectedKey(best);

      const open = getServicePaths(pathname);
      setOpenKeys(open ? [open] : []);
    } else {
      const best = getBestMatchKey(pathname, shopMenuKeys);
      setSelectedKey(best);

      const open = getShopOpenKey(pathname);
      setOpenKeys(!sidebarCollapse && open ? [open] : []);
    }
  }, [pathname, shopInfo?.id, sidebarCollapse, adminMenuKeys, shopMenuKeys]);
  const handleOpenChange = (keys: string[]) => {
    setOpenKeys((prev) => {
      const latestOpenKey = keys.find((k) => !prev.includes(k));
      if (!latestOpenKey) return [];
      return [latestOpenKey];
    });
  };

  return (
    <>
      <Sider
        width={300}
        collapsible
        collapsed={sidebarCollapse}
        onCollapse={(value) => {
          setCollapsed(value);
        }}
      >
        <div className="logo sticky top-0 z-10">
          <Link to="/">
            <img
              src={sidebarCollapse ? IconImg : LogoImg}
              alt="jachai parcel"
              className="logoImg"
            />
          </Link>
        </div>

        {!sidebarCollapse &&
          (shopInfo?.id == null || shopInfo?.id == "" ? (
            <div className="bg-transparent text-white shadow-gray-200 border-b mb-4">
              <div className="w-32 h-32 rounded-full bg-gray-100 overflow-hidden mt-5 ml-[25%] relative">
                <img
                  src={
                    merchantInfo?.storeDetails?.profilePicture ||
                    merchantInfo?.merchantPersonalDetail?.profilePicture ||
                    noImage
                  }
                  alt="merchant profile"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>

              <div className="px-4 pb-2">
                <div className=" text-center">
                  <h2 className="text-lg text-white text-center font-bold">
                    {shopInfo?.name ||
                      merchantInfo?.merchantPersonalDetail?.name ||
                      merchantInfo?.storeDetails?.storeName?.toUpperCase() ||
                      merchantInfo?.fullName?.toUpperCase()}
                  </h2>

                  <Rate
                    value={shopInfo?.rating || merchantInfo?.rating || 1}
                    disabled
                  />
                  <span className="bg-blue-100 text-blue-800 text-xs  font-semibold mr-2 px-1 py-0.5 rounded-full dark:bg-blue-200 dark:text-blue-800 ml-2">
                    {shopInfo?.numberOfRating ||
                      merchantInfo?.numberOfRating ||
                      0}
                  </span>
                </div>

                <div className="mb-2 text-sm font-medium bg-gray-300 text-slate-800 rounded-md px-2 py-0.5 mt-2 h-10">
                  <div className="w-9/12 float-left h-full text-start mt-2">
                    {shopInfo?.mobileNumber?.replace("+88", "") ||
                      merchantInfo?.mobileNumber?.replace("+88", "")}
                  </div>
                  <div className="w-3/12 float-left h-full text-right mt-2">
                    {merchantInfo?.isMobileVerified === true ? (
                      <p className="text-green-700 font-bold size-12">
                        Verified
                      </p>
                    ) : (
                      <p className=" text-red-700 font-bold size-12">
                        Unverified
                      </p>
                    )}
                  </div>
                </div>

                <div
                  className="mb-2 text-sm font-medium text-slate-800 rounded-md px-2 py-5 mt-2"
                  style={{ backgroundColor: "#047E94" }}
                >
                  <Row>
                    <Col span={16}>
                      <h2 className="text-lg font-bold text-white">
                        Jachai Membership
                      </h2>
                      <p className="text-white text-xs">
                        Explore offers with a new seller and enjoy more
                        benefits.
                      </p>
                    </Col>
                    <Col span={8}>
                      <div className="hover:from-pink-500 hover:to-yellow-500 text-white font-bold py-1 mt-6 px-4 rounded-full shadow-md bg-gradient-to-r from-indigo-500 via-pink-500 to-red-500">
                        <Link to="/membership-level">Upgrade</Link>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-transparent text-white shadow-gray-200 border-b mb-4">
              <div className="sm:flex__ xl:block sm:space-x-4 xl:space-x-0">
                <div className="w-full overflow-auto h-48">
                  <img
                    src={shopInfo?.banner || noImage}
                    alt=""
                    className="object-cover w-full"
                  />
                </div>
                <div className="px-4 pb-2">
                  <div className="-mt-12 xl:-mt-20">
                    <Avatar
                      size={100}
                      shape="circle"
                      src={getImgUrl(shopInfo?.logo)}
                      className="mb-5 w-20 h-20 shadow-lg border-4 border-white bg-gray-50"
                    />
                  </div>
                  <div>
                    <h2 className="text-xl text-white font-bold -mt-4">
                      {shopInfo?.name?.toUpperCase()}
                    </h2>

                    {shopInfo?.rating && (
                      <>
                        <Rate
                          value={shopInfo?.rating || 1}
                          className="mt-1"
                          disabled
                        />
                        <span className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded dark:bg-blue-200 dark:text-blue-800 ml-3">
                          {shopInfo?.numberOfRating || 0}
                        </span>
                      </>
                    )}
                  </div>

                  {shopInfo?.mobileNumber && (
                    <div className="mb-4 sm:flex xl:block">
                      <div className="sm:flex-1">
                        <address className="text-sm not-italic font-normal text-gray-500">
                          <div className="mt-4">Mobile Number</div>
                          <div className="mb-2 text-sm font-medium text-white">
                            {shopInfo?.mobileNumber?.split("+88")}
                          </div>
                        </address>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={selectedKey ? [selectedKey] : []}
          openKeys={sidebarCollapse ? [] : openKeys}
          onOpenChange={handleOpenChange}
        >
          {shopInfo?.id == null || shopInfo?.id == "" ? (
            <>
              {adminServices?.map((service: any) => {
                if (service?.sub && service?.sub?.length) {
                  if (
                    service?.sub?.filter((item: any) => {
                      const excludeIcon = item;
                      return (
                        excludeIcon?.icon &&
                        isAllowedService(`${service.name} ${item.name}`)
                      );
                    })?.length === 1 &&
                    service?.sub?.find((item: any) => item.isParent)
                  ) {
                    const actualPath = `${service?.path}${service?.sub?.[0]?.path}`;

                    return (
                      <Menu.Item
                        key={actualPath}
                        icon={<FontAwesomeIcon icon={service?.icon} />}
                      >
                        <Link to={actualPath}>
                          {type == "JC_FOOD"
                            ? service?.name
                                ?.replace("Shops", "Restaurants")
                                ?.replace("Product", "Menu")
                            : service?.name}
                        </Link>
                      </Menu.Item>
                    );
                  } else {
                    return (
                      service?.sub?.find((item: any) =>
                        isAllowedService(`${service.name} ${item.name}`)
                      ) && (
                        <SubMenu
                          key={service?.path}
                          icon={<FontAwesomeIcon icon={service?.icon} />}
                          title={service?.name?.replace("Shops", "Restaurants")}
                        >
                          {service?.sub?.map((sub: any) => {
                            const actualPath = `${service?.path}${sub?.path}`;
                            const excludeIcon = sub;

                            return (
                              !actualPath.includes(":") &&
                              excludeIcon?.icon &&
                              isAllowedService(`${service.name} ${sub.name}`) && (
                                <Menu.Item
                                  key={actualPath}
                                  icon={<FontAwesomeIcon icon={sub?.icon} />}
                                >
                                  <Link to={actualPath}>{sub?.name}</Link>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  }
                } else {
                  return (
                    isAllowedService(`${service.name}`) && (
                      <Menu.Item
                        key={service?.path}
                        icon={<FontAwesomeIcon icon={service?.icon} />}
                      >
                        <Link to={service?.path as any}>{service?.name}</Link>
                      </Menu.Item>
                    )
                  );
                }
              })}
            </>
          ) : (
            <>
              <Menu.Item key="/" icon={<AppstoreOutlined />}>
                <Link to={`/`}>Dashboard</Link>
              </Menu.Item>

              <SubMenu
                key={"sub4"}
                icon={<ShoppingCartOutlined />}
                title={"Orders & Review"}
              >
                <Menu.Item
                  key={`/shops/${shopInfo?.id}/myshop-orders`}
                  icon={<UnorderedListOutlined />}
                >
                  <Link to={`/shops/${shopInfo?.id}/myshop-orders`}>
                    Orders List
                  </Link>
                </Menu.Item>

                <Menu.Item
                  key={`/shops/${shopInfo?.id}/myshop-return-order`}
                  icon={<ShoppingOutlined />}
                >
                  <Link to={`/shops/${shopInfo?.id}/myshop-return-order`}>
                    Return Orders
                  </Link>
                </Menu.Item>
              </SubMenu>

              {type !== "JC_BOOKING" && type === "JC_AFFILIATE" ? (
                <Menu.Item
                  key={`/shops/${shopInfo?.id}/affiliate-products`}
                  icon={<ProductOutlined />}
                >
                  <Link to={`/shops/${shopInfo?.id}/affiliate-products`}>
                    Product List
                  </Link>
                </Menu.Item>
              ) : (
                <>
                  <SubMenu
                    key={"sub2"}
                    icon={<ProductOutlined />}
                    title={"Product Management"}
                  >
                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/my-shop-products`}
                      icon={<FileDoneOutlined />}
                    >
                      <Link to={`/shops/${shopInfo?.id}/my-shop-products`}>
                        My Product
                      </Link>
                    </Menu.Item>

                    {merchantInfo?.type !== "RESELLER" && (
                      <Menu.Item
                        key={`/shops/${shopInfo?.id}/myshop-requested-products`}
                        icon={<TagOutlined />}
                      >
                        <Link
                          to={`/shops/${shopInfo?.id}/myshop-requested-products`}
                        >
                          Requested Products
                        </Link>
                      </Menu.Item>
                    )}

                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/myshop-product-catalogue`}
                      icon={<CopyOutlined />}
                    >
                      <Link
                        to={`/shops/${shopInfo?.id}/myshop-product-catalogue`}
                      >
                        Product Catalogue
                      </Link>
                    </Menu.Item>

                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/myshop-out-of-stock`}
                      icon={<FileSearchOutlined />}
                    >
                      <Link to={`/shops/${shopInfo?.id}/myshop-out-of-stock`}>
                        Out of Stock
                      </Link>
                    </Menu.Item>
                  </SubMenu>

                  <SubMenu
                    key={"sub20"}
                    icon={<ProductOutlined />}
                    title={"Settlement"}
                  >
                    <Menu.Item
                      key={`/my-shop/${shopInfo?.id}/unsettled-orders`}
                      icon={<FileDoneOutlined />}
                    >
                      <Link to={`/my-shop/${shopInfo?.id}/unsettled-orders`}>
                        Unsettled Orders
                      </Link>
                    </Menu.Item>

                    <Menu.Item
                      key={`/my-shop/${shopInfo?.id}/settled-orders`}
                      icon={<FileDoneOutlined />}
                    >
                      <Link to={`/my-shop/${shopInfo?.id}/settled-orders`}>
                        Settled Orders
                      </Link>
                    </Menu.Item>
                  </SubMenu>

                  <Menu.Item
                    key={`/shops/${shopInfo?.id}/myshop-categories`}
                    icon={<ApartmentOutlined />}
                  >
                    <Link to={`/shops/${shopInfo?.id}/myshop-categories`}>
                      Customize Category
                    </Link>
                  </Menu.Item>

                  <SubMenu
                    key={"sub3"}
                    icon={<GiftOutlined />}
                    title={"My Promotion"}
                  >
                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/myshop-campaign`}
                      icon={<SoundOutlined />}
                    >
                      <Link to={`/shops/${shopInfo?.id}/myshop-campaign`}>
                        Campaign
                      </Link>
                    </Menu.Item>

                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/myshop-promo-code`}
                      icon={<CodepenOutlined />}
                    >
                      <Link to={`/shops/${shopInfo?.id}/myshop-promo-code`}>
                        Promo Code
                      </Link>
                    </Menu.Item>
                  </SubMenu>

                  <SubMenu
                    key={"sub1"}
                    icon={<IdcardOutlined />}
                    title={"Basic Information"}
                  >
                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/myshop-details`}
                      icon={<IdcardOutlined />}
                    >
                      <Link to={`/shops/${shopInfo?.id}/myshop-details`}>
                        General Info
                      </Link>
                    </Menu.Item>

                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/shop-business-contract-info`}
                      icon={<ContainerOutlined />}
                    >
                      <Link
                        to={`/shops/${shopInfo?.id}/shop-business-contract-info`}
                      >
                        Business & Contract Info
                      </Link>
                    </Menu.Item>

                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/shop-business-info`}
                      icon={<CarryOutOutlined />}
                    >
                      <Link to={`/shops/${shopInfo?.id}/shop-business-info`}>
                        Business Info
                      </Link>
                    </Menu.Item>

                    <Menu.Item
                      key={`/shops/${shopInfo?.id}/shop-banks`}
                      icon={<BankOutlined />}
                    >
                      <Link to={`/shops/${shopInfo?.id}/shop-banks`}>
                        Bank Info
                      </Link>
                    </Menu.Item>
                  </SubMenu>
                </>
              )}

              <Menu.Item
                key={`/shops/${shopInfo?.id}/chats`}
                icon={<WechatOutlined />}
              >
                <Link to={`/shops/${shopInfo?.id}/chats`}>Chats</Link>
              </Menu.Item>

              <Menu.Item key="switch-to-merchant" icon={<SyncOutlined />}>
                <Popconfirm
                  placement="left"
                  title="Do you want to login this Merchant?"
                  visible={shopInfo?.id === visiblePopconfirm}
                  onConfirm={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSetDefault(shopInfo?.id);
                  }}
                  okButtonProps={{
                    loading: confirmLoading,
                    className: "bg-blue-400",
                    shape: "round",
                  }}
                  cancelButtonProps={{
                    danger: true,
                    type: "primary",
                    shape: "round",
                  }}
                  okText="Yes"
                  cancelText="Not now"
                  onCancel={handleCancel}
                >
                  <Link
                    to={""}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      showPopconfirm(shopInfo?.id);
                    }}
                  >
                    Switch to Merchant
                  </Link>
                </Popconfirm>
              </Menu.Item>
            </>
          )}
        </Menu>
      </Sider>
    </>
  );
};

export default MainSidebar;
